// Compliance deadline calendar: generates each client's SA statutory deadlines
// from its profile, with one-click "Add to Google Calendar" + .ics export.
// Dates are ESTIMATES (eFiling deadlines & VAT categories vary) — verify with
// SARS/CIPC. The value is the reminder + calendar hand-off, not legal precision.
import React, { useState, useMemo } from 'react';

// ── date helpers (local-time, TZ-stable YYYY-MM-DD) ──
const fmtISO = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
const lastDay = (month) => new Date(2025, month, 0).getDate(); // month 1-12 (2025 = non-leap)
function monthlyOcc(day, from, to) {
  const out = []; let d = new Date(from.getFullYear(), from.getMonth(), day);
  while (d < from) d = new Date(d.getFullYear(), d.getMonth() + 1, day);
  while (d <= to) { out.push(fmtISO(d)); d = new Date(d.getFullYear(), d.getMonth() + 1, day); }
  return out;
}
function bimonthlyOcc(day, from, to) {
  const out = []; let m = from.getMonth(), y = from.getFullYear();
  if (m % 2 !== 0) { m++; if (m > 11) { m = 0; y++; } }
  let d = new Date(y, m, day);
  while (d < from) { m += 2; if (m > 11) { m -= 12; y++; } d = new Date(y, m, day); }
  while (d <= to) { out.push(fmtISO(d)); m += 2; if (m > 11) { m -= 12; y++; } d = new Date(y, m, day); }
  return out;
}
function yearlyOcc(month, day, from, to) {
  const out = [];
  for (let y = from.getFullYear() - 1; y <= to.getFullYear() + 1; y++) {
    const d = new Date(y, month - 1, day);
    if (d >= from && d <= to) out.push(fmtISO(d));
  }
  return out;
}
function yeMD(p) {
  const m = String(p.yearEnd || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? { month: +m[2], day: +m[3] } : { month: 2, day: 28 }; // default Feb year-end
}

export function defaultCompliance(p) {
  const c = (p && p.compliance) || {};
  const ye = (p && p.yearEnd) || '';
  return {
    vat: c.vat != null ? c.vat : !!(p && p.vatNumber && String(p.vatNumber).trim()),
    vatFreq: c.vatFreq || 'bimonthly',
    payroll: c.payroll != null ? c.payroll : false,
    provTax: c.provTax != null ? c.provTax : !!ye,
    incomeTax: c.incomeTax != null ? c.incomeTax : !!ye,
    cipc: c.cipc != null ? c.cipc : false,
    cipcMonth: c.cipcMonth || 0,
  };
}

export function deadlinesFor(project, fromISO, toISO) {
  const c = defaultCompliance(project);
  const from = new Date(fromISO + 'T00:00:00'), to = new Date(toISO + 'T00:00:00');
  const out = [];
  const push = (dateISO, type, title) => out.push({ date: dateISO, type, title, projectId: project.id, projectName: project.name });
  if (c.payroll) monthlyOcc(7, from, to).forEach((d) => push(d, 'EMP201', 'EMP201 (PAYE / UIF / SDL)'));
  if (c.vat) {
    if (c.vatFreq === 'monthly') monthlyOcc(25, from, to).forEach((d) => push(d, 'VAT201', 'VAT201 (monthly)'));
    else bimonthlyOcc(25, from, to).forEach((d) => push(d, 'VAT201', 'VAT201 (bi-monthly)'));
  }
  const { month, day } = yeMD(project);
  if (c.provTax) {
    yearlyOcc(month, day, from, to).forEach((d) => push(d, 'IRP6', '2nd provisional tax (IRP6)'));
    const m1 = ((month - 6 + 12 - 1) % 12) + 1;
    yearlyOcc(m1, lastDay(m1), from, to).forEach((d) => push(d, 'IRP6', '1st provisional tax (IRP6)'));
  }
  if (c.incomeTax) yearlyOcc(month, day, from, to).forEach((d) => push(d, 'ITR14', 'Income tax return (ITR14)'));
  if (c.cipc && c.cipcMonth) yearlyOcc(c.cipcMonth, lastDay(c.cipcMonth), from, to).forEach((d) => push(d, 'CIPC', 'CIPC annual return'));
  return out;
}

// ── calendar hand-off ──
const nextDayISO = (iso) => { const d = new Date(iso + 'T00:00:00'); d.setDate(d.getDate() + 1); return fmtISO(d); };
function gcalLink(title, dateISO, details) {
  const s = dateISO.replace(/-/g, ''), e = nextDayISO(dateISO).replace(/-/g, '');
  return 'https://calendar.google.com/calendar/render?action=TEMPLATE&text=' + encodeURIComponent(title) + '&dates=' + s + '/' + e + '&details=' + encodeURIComponent(details || '');
}
function icsBody(events) {
  const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Tlhakiso//Compliance//EN', 'CALSCALE:GREGORIAN'];
  events.forEach((ev, i) => {
    lines.push('BEGIN:VEVENT', 'UID:tlh-' + ev.dateISO.replace(/-/g, '') + '-' + i + '@tlhakiso', 'DTSTART;VALUE=DATE:' + ev.dateISO.replace(/-/g, ''), 'DTEND;VALUE=DATE:' + nextDayISO(ev.dateISO).replace(/-/g, ''), 'SUMMARY:' + ev.title, 'DESCRIPTION:' + String(ev.details || '').replace(/\n/g, '\\n'), 'BEGIN:VALARM', 'TRIGGER:-P3D', 'ACTION:DISPLAY', 'DESCRIPTION:Reminder', 'END:VALARM', 'END:VEVENT');
  });
  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}
function downloadIcs(filename, events) {
  const blob = new Blob([icsBody(events)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}

const TYPE_COLOR = { EMP201: '#3d567a', VAT201: '#9a7d3d', IRP6: '#b3623d', ITR14: '#7a3d6e', CIPC: '#3d7a52' };
const inp = { width: '100%', padding: '7px 9px', fontSize: 12, border: '1px solid #e0ddd8', borderRadius: 5, boxSizing: 'border-box', marginTop: 3 };
const btnP = { padding: '8px 14px', borderRadius: 6, border: 'none', background: '#1a1a2e', color: '#c8a24e', fontSize: 12, fontWeight: 600, cursor: 'pointer' };

export function ComplianceView({ projects, onSave }) {
  const today = new Date();
  const fromISO = fmtISO(today);
  const toISO = fmtISO(new Date(today.getFullYear() + 1, today.getMonth(), today.getDate()));
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const deadlines = useMemo(() => {
    const all = [];
    (projects || []).forEach((p) => all.push(...deadlinesFor(p, fromISO, toISO)));
    return all.sort((a, b) => a.date.localeCompare(b.date));
  }, [projects, fromISO, toISO]);

  const detailsFor = (d) => d.title + ' — ' + d.projectName + ' (estimated SA statutory deadline; verify with SARS/CIPC).';
  const eventsFor = (d) => [{ title: d.title + ' · ' + d.projectName, dateISO: d.date, details: detailsFor(d) }];
  const daysUntil = (iso) => Math.round((new Date(iso + 'T00:00:00') - new Date(fromISO + 'T00:00:00')) / 86400000);

  function openEdit(p) { setEditId(p.id); setDraft(defaultCompliance(p)); }
  async function save() {
    setBusy(true);
    try { await onSave(editId, draft); setEditId(null); setDraft(null); }
    catch (e) { alert('Save failed: ' + e.message); }
    setBusy(false);
  }

  // group deadlines by month label
  const groups = [];
  deadlines.forEach((d) => {
    const key = d.date.slice(0, 7);
    let g = groups.find((x) => x.key === key);
    if (!g) { g = { key, items: [] }; groups.push(g); }
    g.items.push(d);
  });
  const monthLabel = (ym) => { const [y, m] = ym.split('-'); return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][+m - 1] + ' ' + y; };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>Compliance deadlines</div>
          <div style={{ fontSize: 11, color: '#9a9488', marginTop: 2 }}>SA statutory deadlines across all clients · next 12 months. <strong>Estimates — verify with SARS/CIPC.</strong></div>
        </div>
        {deadlines.length > 0 && <button onClick={() => downloadIcs('Tlhakiso_compliance.ics', deadlines.map((d) => ({ title: d.title + ' · ' + d.projectName, dateISO: d.date, details: detailsFor(d) })))} style={btnP}>⤓ All to calendar (.ics)</button>}
      </div>

      <div style={{ display: 'flex', gap: 16, marginTop: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Deadline list */}
        <div style={{ flex: '1 1 540px', minWidth: 340 }}>
          {deadlines.length === 0 && <div style={{ fontSize: 12, color: '#9a9488', background: '#fff', border: '1px solid #eceae4', borderRadius: 8, padding: 14 }}>No deadlines generated. Set each client's obligations on the right (VAT, payroll, provisional/income tax, CIPC).</div>}
          {groups.map((g) => (
            <div key={g.key} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#9a9488', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>{monthLabel(g.key)}</div>
              <div style={{ background: '#fff', border: '1px solid #eceae4', borderRadius: 8, overflow: 'hidden' }}>
                {g.items.map((d, i) => {
                  const du = daysUntil(d.date);
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderTop: i ? '1px solid #f4f2ed' : 'none' }}>
                      <div style={{ width: 84, fontSize: 12, color: '#1a1a2e', fontWeight: 600 }}>{d.date.slice(8)} {monthLabel(d.date.slice(0, 7)).slice(0, 3)}<div style={{ fontSize: 9, color: du < 0 ? '#b33' : du <= 7 ? '#b3623d' : '#9a9488', fontWeight: 400 }}>{du < 0 ? 'overdue' : 'in ' + du + 'd'}</div></div>
                      <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 10, background: (TYPE_COLOR[d.type] || '#777') + '22', color: TYPE_COLOR[d.type] || '#777', fontWeight: 700 }}>{d.type}</span>
                      <div style={{ flex: 1 }}><div style={{ fontSize: 12, color: '#1a1a2e' }}>{d.title}</div><div style={{ fontSize: 10, color: '#9a9488' }}>{d.projectName}</div></div>
                      <a href={gcalLink(d.title + ' · ' + d.projectName, d.date, detailsFor(d))} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#3d567a', textDecoration: 'none', border: '1px solid #d2dae8', borderRadius: 5, padding: '4px 8px', whiteSpace: 'nowrap' }}>📅 Google</a>
                      <button onClick={() => downloadIcs((d.type + '_' + d.projectName).replace(/[^\w]+/g, '_') + '.ics', eventsFor(d))} style={{ fontSize: 11, color: '#666', border: '1px solid #e0ddd8', borderRadius: 5, padding: '4px 8px', background: '#fff', cursor: 'pointer' }}>.ics</button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Per-client obligations */}
        <div style={{ flex: '0 1 320px', minWidth: 280, background: '#fff', border: '1px solid #eceae4', borderRadius: 8, padding: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>Client obligations</div>
          {!editId && (projects || []).map((p) => {
            const c = defaultCompliance(p);
            const on = [c.vat && 'VAT', c.payroll && 'PAYE', c.provTax && 'Prov', c.incomeTax && 'ITR14', c.cipc && 'CIPC'].filter(Boolean).join(' · ') || 'none set';
            return (
              <div key={p.id} onClick={() => openEdit(p)} style={{ padding: '8px 6px', borderTop: '1px solid #f4f2ed', cursor: 'pointer' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a2e' }}>{p.name}</div>
                <div style={{ fontSize: 10, color: '#9a9488' }}>{on}</div>
              </div>
            );
          })}
          {editId && draft && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a2e', marginBottom: 8 }}>{(projects.find((p) => p.id === editId) || {}).name}</div>
              {[['vat', 'VAT201 (VAT registered)'], ['payroll', 'EMP201 (has employees)'], ['provTax', 'Provisional tax (IRP6)'], ['incomeTax', 'Income tax return (ITR14)'], ['cipc', 'CIPC annual return']].map(([k, lbl]) => (
                <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#444', padding: '4px 0' }}>
                  <input type="checkbox" checked={!!draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.checked })} /> {lbl}
                </label>
              ))}
              {draft.vat && (
                <label style={{ display: 'block', fontSize: 11, color: '#7a7568', marginTop: 6 }}>VAT frequency
                  <select value={draft.vatFreq} onChange={(e) => setDraft({ ...draft, vatFreq: e.target.value })} style={inp}><option value="bimonthly">Bi-monthly (Cat A/B)</option><option value="monthly">Monthly (Cat C)</option></select>
                </label>
              )}
              {draft.cipc && (
                <label style={{ display: 'block', fontSize: 11, color: '#7a7568', marginTop: 6 }}>CIPC anniversary month
                  <select value={draft.cipcMonth || ''} onChange={(e) => setDraft({ ...draft, cipcMonth: Number(e.target.value) })} style={inp}><option value="">—</option>{['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select>
                </label>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button disabled={busy} onClick={save} style={btnP}>{busy ? 'Saving…' : 'Save'}</button>
                <button onClick={() => { setEditId(null); setDraft(null); }} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #e0ddd8', background: '#fff', color: '#444', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
